import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, ShieldAlert, RefreshCw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

function generatePassword(len = 14) {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += charset[arr[i] % charset.length];
  return out;
}

export function ChangePasswordModal({ open, onOpenChange, user, onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [notify, setNotify] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPassword(""); setConfirm(""); setShow(false); setNotify(true); };

  const submit = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    if (!window.confirm(`Override password for ${user?.email}? The user will be notified by email if enabled.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { password, notify_user: notify });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 brand-orange" /> Override user password
          </DialogTitle>
          <DialogDescription>
            Set a new password for <span className="font-semibold text-foreground">{user?.email}</span>.
            The current password is never displayed and will be replaced.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2 text-xs text-foreground/70">
          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>This action is logged in the user&rsquo;s activity timeline and attributed to your admin account.</div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="newpw">New password</Label>
              <button
                type="button"
                onClick={() => { const p = generatePassword(); setPassword(p); setConfirm(p); setShow(true); }}
                className="text-xs brand-orange font-semibold hover:underline flex items-center gap-1"
                data-testid="generate-password-btn"
              >
                <RefreshCw className="w-3 h-3" /> Generate strong
              </button>
            </div>
            <div className="relative mt-1.5">
              <Input
                id="newpw"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                className="pr-10 font-mono"
                data-testid="new-password-input"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/50">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="confirmpw">Confirm password</Label>
            <Input
              id="confirmpw"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={6}
              className="mt-1.5 font-mono"
              data-testid="confirm-password-input"
            />
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="notify-user-switch" />
            <span>Email user to notify them of the change</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !password || !confirm} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid="submit-change-password">
            {busy ? "Updating…" : "Override password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
